import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Route, MapPin, DollarSign } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import RangoFleteForm from './RangoFleteForm';
import apiService from '../../services/apiService';
import { formatMoney } from '../../utils/formatters';

const RangosFleteTab = () => {
    const { loading, setLoading, setError, clearError } = useApp();

    const [rangos, setRangos] = useState([]);
    const [filteredRangos, setFilteredRangos] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedRango, setSelectedRango] = useState(null);

    useEffect(() => {
        loadRangos();
    }, []);

    useEffect(() => {
        filterRangos();
    }, [rangos, searchQuery]);

    const loadRangos = async () => {
        try {
            setLoading(true);
            clearError();

            const result = await apiService.getRangosFlete();

            if (result.success) {
                setRangos(result.data);
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al cargar rangos de flete');
        } finally {
            setLoading(false);
        }
    };

    const filterRangos = () => {
        if (!searchQuery.trim()) {
            setFilteredRangos(rangos);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = rangos.filter(rango =>
            rango.vehiculo_nombre?.toLowerCase().includes(query) ||
            rango.vehiculo_placa?.toLowerCase().includes(query) ||
            rango.distancia_desde.toString().includes(query) ||
            rango.distancia_hasta.toString().includes(query) ||
            rango.costo.toString().includes(query)
        );

        setFilteredRangos(filtered);
    };

    const handleCreateRango = async (rangoData) => {
        try {
            const result = await apiService.crearRangoFlete(rangoData);

            if (result.success) {
                setShowCreateModal(false);
                loadRangos();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al crear rango de flete');
        }
    };

    const handleEditRango = async (rangoData) => {
        try {
            const result = await apiService.actualizarRangoFlete({
                id: selectedRango.id,
                ...rangoData
            });

            if (result.success) {
                setShowEditModal(false);
                setSelectedRango(null);
                loadRangos();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al actualizar rango de flete');
        }
    };

    const handleDeleteRango = async (id, distancia) => {
        if (!window.confirm(`¿Estás seguro de que quieres eliminar el rango de ${distancia.desde}-${distancia.hasta} km?`)) {
            return;
        }

        try {
            const result = await apiService.eliminarRangoFlete(id);

            if (result.success) {
                loadRangos();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al eliminar rango de flete');
        }
    };


    return (
        <div className="rangos-flete-tab">
            {/* Controles */}
            <div className="rangos-controls">
                <div className="search-container">
                    <Input
                        placeholder="Buscar por vehículo, distancia o costo..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        icon={<Search size={20} />}
                        className="search-input"
                    />
                </div>

                <div className="controls-actions">
                    <Button
                        onClick={() => setShowCreateModal(true)}
                        disabled={loading}
                    >

                        Nuevo Rango
                    </Button>
                </div>
            </div>

            {/* Información de resumen */}
            <div className="rangos-summary">
                <span className="rangos-count">
                    {filteredRangos.length} de {rangos.length} rangos
                </span>
            </div>

            {/* Tabla de rangos */}
            <div className="rangos-table-container">
                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Cargando rangos...</p>
                    </div>
                )}

                {!loading && filteredRangos.length === 0 && (
                    <div className="empty-state">
                        <Route size={48} />
                        <h3>No se encontraron rangos</h3>
                        <p>
                            {searchQuery
                                ? 'No hay rangos que coincidan con tu búsqueda'
                                : 'No hay rangos de flete configurados'
                            }
                        </p>
                        {!searchQuery && (
                            <Button
                                onClick={() => setShowCreateModal(true)}
                                className="empty-action-btn"
                            >

                                Crear primer rango
                            </Button>
                        )}
                    </div>
                )}

                {!loading && filteredRangos.length > 0 && (
                    <table className="rangos-table-compact">
                        <thead>
                            <tr>
                                <th width="30%">Rango de Distancia</th>
                                <th width="25%">Vehículo</th>
                                <th width="10%" data-type="numeric">Costo</th>
                                <th width="35%" data-type="actions">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRangos.map((rango) => (
                                <tr key={rango.id} className={rango.activo ? '' : 'inactive-row'}>
                                    <td>
                                        <div className="distancia-info">
                                            <div className="distancia-range">
                                                <MapPin size={14} />
                                                {rango.distancia_desde} - {rango.distancia_hasta} km
                                            </div>
                                        </div>
                                    </td>

                                    <td>
                                        <div className="vehiculo-info">
                                            <div className="vehiculo-nombre">
                                                {rango.vehiculo_nombre || 'Sin vehículo asignado'}
                                            </div>
                                        </div>
                                    </td>

                                    <td>
                                        <div className="costo-info">
                                            <div className="costo-valor">

                                                {formatMoney(rango.costo)}
                                            </div>
                                        </div>
                                    </td>

                                    <td>
                                        <div className="actions-compact">
                                            <button
                                                className="action-btn-compact"
                                                onClick={() => {
                                                    setSelectedRango(rango);
                                                    setShowEditModal(true);
                                                }}
                                                title="Editar"
                                            >
                                                <Edit size={14} />
                                            </button>

                                            <button
                                                className="action-btn-compact delete"
                                                onClick={() => handleDeleteRango(rango.id, {
                                                    desde: rango.distancia_desde,
                                                    hasta: rango.distancia_hasta
                                                })}
                                                title="Eliminar"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modales */}
            {showCreateModal && (
                <Modal
                    title="Crear Nuevo Rango de Flete"
                    onClose={() => setShowCreateModal(false)}
                    size="large"
                >
                    <RangoFleteForm
                        onSubmit={handleCreateRango}
                        onCancel={() => setShowCreateModal(false)}
                        isLoading={loading}
                    />
                </Modal>
            )}

            {showEditModal && selectedRango && (
                <Modal
                    title="Editar Rango de Flete"
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedRango(null);
                    }}
                    size="large"
                >
                    <RangoFleteForm
                        rango={selectedRango}
                        onSubmit={handleEditRango}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedRango(null);
                        }}
                        isLoading={loading}
                        isEdit={true}
                    />
                </Modal>
            )}
        </div>
    );
};

export default RangosFleteTab;